import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { updateUserProfile } from '../services/userService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config';

const ProfilePage: React.FC = () => {
  const { user, logout, updateUserState } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0] || !user) return;
    try {
      setLoading(true);
      setError(null);
      const file = e.target.files[0];
      const storageRef = ref(storage, `user-photos/${user.id}`);
      await uploadBytes(storageRef, file);
      const photoURL = await getDownloadURL(storageRef);
      await updateUserProfile(user.id, { photoURL });
      updateUserState({ photoURL });
    } catch {
      setError(t('profile.errors.photoUploadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    try {
      setLoading(true);
      setError(null);
      await updateUserProfile(user.id, { name, phone });
      updateUserState({ name, phone });
      setEditing(false);
    } catch {
      setError(t('profile.errors.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch {}
  };

  if (!user) return null;

  return (
    <div className="bg-background min-h-screen">
      <div className="max-w-container mx-auto px-4 md:px-margin-desktop py-12">
        <div className="max-w-2xl mx-auto">

          {/* Profile card */}
          <div className="bg-surface-container-lowest rounded-2xl card-elevation overflow-hidden">

            {/* Header */}
            <div className="bg-primary px-6 py-5">
              <h1 className="font-headline text-headline-sm text-on-primary">{t('profile.title')}</h1>
            </div>

            {/* Avatar */}
            <div className="flex flex-col items-center px-6 py-8 border-b border-outline-variant">
              <div className="relative mb-4">
                <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-primary bg-surface-container">
                  {user.photoURL ? (
                    <img src={user.photoURL} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="material-symbols-outlined text-[56px] text-on-surface-variant">person</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                  className="absolute bottom-0 right-0 w-9 h-9 bg-primary text-on-primary rounded-full flex items-center justify-center hover:brightness-110 transition-all active:scale-95 disabled:opacity-50"
                >
                  <span className="material-symbols-outlined text-[18px]">photo_camera</span>
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
              </div>
              <p className="font-headline text-headline-sm text-on-surface">{user.name}</p>
              <p className="font-body text-body-md text-on-surface-variant">{user.email}</p>
            </div>

            {/* Fields */}
            <div className="divide-y divide-outline-variant">
              {/* Name */}
              <div className="flex items-center gap-4 px-6 py-4">
                <div className="w-10 h-10 rounded-lg bg-secondary-container flex items-center justify-center flex-shrink-0">
                  <span className="material-symbols-outlined text-on-secondary-container text-[20px]">person</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-headline text-label-sm text-on-surface-variant uppercase tracking-widest mb-1">{t('profile.fields.name')}</p>
                  {editing ? (
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full py-2 px-3 bg-surface border border-surface-container-highest rounded-lg focus:outline-none focus:border-primary font-body text-body-md"
                    />
                  ) : (
                    <p className="font-body text-body-md text-on-surface">{name}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="flex items-center gap-4 px-6 py-4">
                <div className="w-10 h-10 rounded-lg bg-secondary-container flex items-center justify-center flex-shrink-0">
                  <span className="material-symbols-outlined text-on-secondary-container text-[20px]">mail</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-headline text-label-sm text-on-surface-variant uppercase tracking-widest mb-1">{t('profile.fields.email')}</p>
                  <p className="font-body text-body-md text-on-surface">{user.email}</p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-center gap-4 px-6 py-4">
                <div className="w-10 h-10 rounded-lg bg-secondary-container flex items-center justify-center flex-shrink-0">
                  <span className="material-symbols-outlined text-on-secondary-container text-[20px]">phone</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-headline text-label-sm text-on-surface-variant uppercase tracking-widest mb-1">{t('profile.fields.phone')}</p>
                  {editing ? (
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder={t('profile.fields.enterPhone')}
                      className="w-full py-2 px-3 bg-surface border border-surface-container-highest rounded-lg focus:outline-none focus:border-primary font-body text-body-md"
                    />
                  ) : (
                    <p className="font-body text-body-md text-on-surface">{phone || t('profile.fields.phoneNotSet')}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="mx-6 mt-4 bg-error-container text-on-error-container px-4 py-3 rounded-lg font-body text-body-md">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="px-6 py-5 flex justify-between items-center border-t border-outline-variant">
              {editing ? (
                <div className="flex gap-3">
                  <button
                    onClick={() => setEditing(false)}
                    disabled={loading}
                    className="px-6 py-2 border border-outline text-on-surface rounded-lg font-headline text-label-md hover:bg-surface-container transition-colors disabled:opacity-50"
                  >
                    {t('profile.buttons.cancel')}
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="px-6 py-2 bg-primary text-on-primary rounded-lg font-headline text-label-md hover:brightness-110 transition-all active:scale-95 disabled:opacity-50"
                  >
                    {loading ? t('profile.buttons.saving') : t('profile.buttons.save')}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setEditing(true)}
                  className="px-6 py-2 bg-primary text-on-primary rounded-lg font-headline text-label-md hover:brightness-110 transition-all active:scale-95"
                >
                  {t('profile.buttons.edit')}
                </button>
              )}

              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-6 py-2 border-2 border-error text-error rounded-lg font-headline text-label-md hover:bg-error hover:text-on-error transition-all active:scale-95"
              >
                <span className="material-symbols-outlined text-[18px]">logout</span>
                {t('profile.buttons.signOut')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
